import { value } from '@takeshape/util';
import { CURRENT_OPEN_API_TRANSFORM_VERSION } from "../constants.js";
import { updateAuthentication } from "../util/authentication.js";
import { hasEveryKey } from "../util/has.js";
import { endpointWithPath, getHealthCheck } from "../util/health.js";
const title = 'Groq';
const specUrl = 'https://storage.googleapis.com/stainless-sdk-openapi-specs/groqcloud%2Fgroqcloud-d1588e103a6ae0234752b8e54a746fb1e4c93a0ee51ede294017bcd4f0ee4ac0.yml';
const serviceProvider = {
    name: 'groq',
    title,
    description: 'The LPU™ Inference Engine by Groq is a hardware and software platform that delivers exceptional compute speed, quality, and energy efficiency. Groq provides cloud and on-prem solutions at scale for AI applications.',
    categories: ['ai'],
    links: [
        { title: 'Homepage', url: 'https://www.groq.com/' },
        { title: 'Docs', url: '/docs/services/providers/groq' }
    ],
    serviceType: 'openapi',
    introspectionType: 'openapi',
    createId: value('groq'),
    getFormSchema: value({
        type: 'object',
        required: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                title: 'Name',
                type: 'string',
                default: 'Groq'
            },
            id: {
                title: 'Slug',
                type: 'string',
                pattern: '[0-9a-z_-]+',
                default: 'groq'
            },
            namespace: {
                title: 'Namespace',
                type: 'string',
                pattern: '^[A-Z][A-Za-z]+$',
                default: 'Groq'
            },
            authentication: {
                title: 'Authentication',
                description: 'The following properties are encrypted and are obscured on this form to ensure the security of your data.',
                type: 'object',
                properties: {
                    token: {
                        title: 'API Key',
                        type: 'string',
                        '@sensitive': true
                    }
                },
                required: ['token']
            }
        }
    }),
    getFormConfig: value({
        order: ['title', 'id', 'namespace', 'authentication'],
        properties: {
            title: {
                widget: 'singleLineText',
                instructions: 'A human-readable title for this service.'
            },
            id: {
                widget: 'singleLineText',
                instructions: 'The human-readable unique identifier for this service. Only lowercase letters, numbers, hyphen, and underscore are allowed.'
            },
            namespace: {
                widget: 'singleLineText',
                instructions: 'A namespace to use for any generated types or queries. For example, if you enter `Foo` your types will be named `Foo_Bar`.'
            },
            authentication: {
                properties: {
                    token: {
                        widget: 'sensitiveField',
                        instructions: 'You can manage API keys for Groq at https://console.groq.com/keys'
                    }
                },
                order: ['token']
            }
        }
    }),
    getConfig(formData) {
        const config = {
            title: formData.title ?? title,
            namespace: formData.namespace ?? title,
            provider: 'groq',
            serviceType: 'openapi',
            authenticationType: 'bearer',
            options: {
                endpoint: 'https://api.groq.com',
                spec: specUrl,
                transformVersion: CURRENT_OPEN_API_TRANSFORM_VERSION
            }
        };
        return updateAuthentication(config, formData, { type: 'bearer' });
    },
    isCompleteConfig: hasEveryKey(['authentication', 'options.endpoint', 'options.spec']),
    getHealthCheck: getHealthCheck({
        checkName: 'ping',
        endpoint: endpointWithPath('/openai/v1/models')
    }),
    getAiModels() {
        return {
            generate: [
                'llama-3.3-70b-versatile',
                'llama-3.1-8b-instant',
                'llama-guard-3-8b',
                'llama3-70b-8192',
                'llama3-8b-8192',
                'gemma2-9b-it',
                'mixtral-8x7b-32768',
                'whisper-large-v3',
                'whisper-large-v3-turbo',
                'mistral-saba-24b',
                'qwen-2.5-coder-32b',
                'qwen-2.5-32b',
                'deepseek-r1-distill-qwen-32b',
                'deepseek-r1-distill-llama-70b-specdec',
                'deepseek-r1-distill-llama-70b',
                'llama-3.3-70b-specdec',
                'llama-3.2-1b-preview',
                'llama-3.2-3b-preview',
                'llama-3.2-11b-vision-preview',
                'llama-3.2-90b-vision-preview'
            ],
            embedding: [],
            image: []
        };
    }
};
export default serviceProvider;
